import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaTasks,
  FaPlus,
  FaEdit,
  FaTrash,
  FaCalendarAlt,
  FaUser,
  FaCheck,
  FaTimes,
  FaExclamationTriangle,
  FaClock,
  FaFilter,
  FaFolder,
  FaChevronDown,
  FaChevronRight,
  FaFlag
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import api from '../../core/api/client';
import { storage } from '../../core/utils/storage';
import { ScrollReveal } from '../../shared/components';
import './TodoList.css';

const TodoList = () => {
  const [todos, setTodos] = useState([]);
  const [categories, setCategories] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingTodo, setEditingTodo] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [viewMode, setViewMode] = useState('today'); // today, week, all, category
  const [expandedCategories, setExpandedCategories] = useState({});
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category_id: null,
    assigned_to_id: null,
    due_date: '',
    priority: 'medium'
  });

  // Category form
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    color: '#3B82F6',
    icon: '📋'
  });

  // Quick add form
  const [quickAddText, setQuickAddText] = useState('');
  const [quickAddForAll, setQuickAddForAll] = useState(true);

  useEffect(() => {
    fetchTodos();
    fetchCategories();
    fetchUsers();
  }, [viewMode, selectedCategory]);

  const fetchTodos = async () => {
    try {
      let params = {};
      if (viewMode === 'today') params.date = 'today';
      else if (viewMode === 'week') params.date = 'week';
      else if (viewMode === 'category' && selectedCategory) params.category_id = selectedCategory;
      
      const response = await api.get('/todos', { params });
      setTodos(response.data);
    } catch (error) {
      toast.error('Chyba pri načítaní úloh');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/todo-categories');
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users/list');
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title) {
      toast.error('Názov úlohy je povinný');
      return;
    }

    try {
      if (editingTodo) {
        await api.put(`/todos/${editingTodo}`, formData);
        toast.success('Úloha aktualizovaná');
      } else {
        await api.post('/todos', formData);
        toast.success('Úloha vytvorená');
      }
      
      setFormData({
        title: '',
        description: '',
        category_id: null,
        assigned_to_id: null,
        due_date: '',
        priority: 'medium'
      });
      setShowAddForm(false);
      setEditingTodo(null);
      fetchTodos();
    } catch (error) {
      toast.error('Chyba pri ukladaní úlohy');
    }
  };

  const handleStatusChange = async (todoId, newStatus) => {
    try {
      await api.put(`/todos/${todoId}`, { status: newStatus });
      fetchTodos();
      if (newStatus === 'completed') {
        toast.success('Úloha dokončená!');
      }
    } catch (error) {
      toast.error('Chyba pri zmene stavu');
    }
  };

  const handleDelete = async (todoId) => {
    if (!window.confirm('Naozaj chcete zmazať túto úlohu?')) return;
    
    try {
      await api.delete(`/todos/${todoId}`);
      toast.success('Úloha zmazaná');
      fetchTodos();
    } catch (error) {
      toast.error('Chyba pri mazaní úlohy');
    }
  };

  const handleCategorySubmit = async (e) => {
    e.preventDefault();

    try {
      await api.post('/todo-categories', categoryForm);
      toast.success('Kategória vytvorená');
      setCategoryForm({ name: '', color: '#3B82F6', icon: '📋' });
      setShowCategoryForm(false);
      fetchCategories();
    } catch (error) {
      toast.error('Chyba pri vytváraní kategórie');
    }
  };

  const handleQuickAdd = async (e) => {
    e.preventDefault();

    if (!quickAddText.trim()) {
      toast.error('Napíšte popis úlohy');
      return;
    }

    try {
      const currentUserId = storage.getUserId();

      const quickTodo = {
        title: quickAddText,
        description: '', // Empty description for quick tasks
        assigned_to_id: quickAddForAll ? null : currentUserId, // null = for all users, userId = for current user
        priority: 'medium'
      };

      await api.post('/todos', quickTodo);
      toast.success('Úloha pridaná!');
      setQuickAddText('');
      fetchTodos();
    } catch (error) {
      toast.error('Chyba pri pridávaní úlohy');
    }
  };

  const getPriorityColor = (priority) => {
    switch(priority) {
      case 'urgent': return '#EF4444';
      case 'high': return '#F59E0B';
      case 'medium': return '#3B82F6';
      case 'low': return '#6B7280';
      default: return '#6B7280';
    }
  };

  const getPriorityIcon = (priority) => {
    switch(priority) {
      case 'urgent': return '🔴';
      case 'high': return '🟠';
      case 'medium': return '🔵';
      case 'low': return '⚪';
      default: return '⚪';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    if (date.toDateString() === today.toDateString()) {
      return 'Dnes';
    } else if (date.toDateString() === tomorrow.toDateString()) {
      return 'Zajtra';
    } else {
      return date.toLocaleDateString('sk-SK');
    }
  };

  // Group todos by date/category
  const groupTodos = () => {
    const grouped = {};
    
    if (viewMode === 'category' || viewMode === 'all') {
      // Group by category
      todos.forEach(todo => {
        const categoryName = todo.category?.name || 'Bez kategórie';
        if (!grouped[categoryName]) {
          grouped[categoryName] = {
            todos: [],
            category: todo.category,
            icon: todo.category?.icon || '📁',
            color: todo.category?.color || '#6B7280'
          };
        }
        grouped[categoryName].todos.push(todo);
      });
    } else {
      // Group by date
      const today = new Date().toDateString();
      const overdue = [];
      const todayTodos = [];
      const upcoming = [];
      const noDueDate = [];
      
      todos.forEach(todo => {
        if (!todo.due_date) {
          noDueDate.push(todo);
        } else {
          const dueDate = new Date(todo.due_date).toDateString();
          if (todo.is_overdue) {
            overdue.push(todo);
          } else if (dueDate === today) {
            todayTodos.push(todo);
          } else {
            upcoming.push(todo);
          }
        }
      });
      
      if (overdue.length) grouped['Premeškané'] = { todos: overdue, color: '#EF4444', icon: '⚠️' };
      if (todayTodos.length) grouped['Dnes'] = { todos: todayTodos, color: '#3B82F6', icon: '📅' };
      if (upcoming.length) grouped['Nadchádzajúce'] = { todos: upcoming, color: '#10B981', icon: '📆' };
      if (noDueDate.length) grouped['Bez dátumu'] = { todos: noDueDate, color: '#6B7280', icon: '📋' };
    }
    
    return grouped;
  };

  const groupedTodos = groupTodos();

  return (
    <div className="todolist-page">
      <div className="page-header">
        <h1><FaTasks /> Úlohy</h1>
        <div className="header-actions">
          <div className="view-selector">
            <button
              className={viewMode === 'today' ? 'active' : ''}
              onClick={() => setViewMode('today')}
            >
              Dnes
            </button>
            <button
              className={viewMode === 'week' ? 'active' : ''}
              onClick={() => setViewMode('week')}
            >
              Týždeň
            </button>
            <button
              className={viewMode === 'all' ? 'active' : ''}
              onClick={() => setViewMode('all')}
            >
              Všetky
            </button>
            <button
              className={viewMode === 'category' ? 'active' : ''}
              onClick={() => setViewMode('category')}
            >
              Kategórie
            </button>
          </div>
          <motion.button
            className="btn btn-primary"
            onClick={() => setShowAddForm(!showAddForm)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <FaPlus /> Nová úloha
          </motion.button>
        </div>
      </div>

      {/* Category filter for category view */}
      {viewMode === 'category' && (
        <div className="category-filter">
          <select
            value={selectedCategory || ''}
            onChange={(e) => setSelectedCategory(e.target.value || null)}
            className="category-select"
          >
            <option value="">Všetky kategórie</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>
                {cat.icon} {cat.name}
              </option>
            ))}
          </select>
          <button
            className="btn btn-secondary"
            onClick={() => setShowCategoryForm(true)}
          >
            <FaPlus /> Nová kategória
          </button>
        </div>
      )}

      {/* Quick Add Form */}
      <motion.div
        className="quick-add-form"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <form onSubmit={handleQuickAdd} className="quick-add-inner">
          <input
            type="text"
            value={quickAddText}
            onChange={(e) => setQuickAddText(e.target.value)}
            placeholder="Rýchle pridanie úlohy... (napíš a stlač Enter)"
            className="quick-add-input"
          />
          <label className="quick-add-checkbox">
            <input
              type="checkbox"
              checked={quickAddForAll}
              onChange={(e) => setQuickAddForAll(e.target.checked)}
            />
            <span>Pre všetkých</span>
          </label>
          <button type="submit" className="btn btn-success quick-add-btn">
            <FaPlus /> Pridať
          </button>
        </form>
      </motion.div>

      {/* Add/Edit Todo Form Modal */}
      <AnimatePresence>
        {showAddForm && (
          <motion.div
            className="todo-modal-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => {
              setShowAddForm(false);
              setEditingTodo(null);
              setFormData({
                title: '',
                description: '',
                category_id: null,
                assigned_to_id: null,
                due_date: '',
                priority: 'medium'
              });
            }}
          >
            <motion.div
              className="todo-form todo-modal"
              initial={{ opacity: 0, scale: 0.92, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.92, y: 20 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
            >
            <div className="todo-modal-header">
              <h2>{editingTodo ? 'Upraviť úlohu' : 'Nová úloha'}</h2>
              <button
                type="button"
                className="todo-modal-close"
                onClick={() => {
                  setShowAddForm(false);
                  setEditingTodo(null);
                  setFormData({
                    title: '',
                    description: '',
                    category_id: null,
                    assigned_to_id: null,
                    due_date: '',
                    priority: 'medium'
                  });
                }}
              >
                <FaTimes />
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-grid">
                <div className="form-group full-width">
                  <label>Názov úlohy *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Čo treba urobiť?"
                    required
                  />
                </div>

                <div className="form-group">
                  <label><FaFolder /> Kategória</label>
                  <select
                    value={formData.category_id || ''}
                    onChange={(e) => setFormData({ ...formData, category_id: e.target.value || null })}
                  >
                    <option value="">Bez kategórie</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>
                        {cat.icon} {cat.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label><FaUser /> Priradiť</label>
                  <select
                    value={formData.assigned_to_id || ''}
                    onChange={(e) => setFormData({ ...formData, assigned_to_id: e.target.value || null })}
                  >
                    <option value="">Pre mňa</option>
                    {users.map(user => (
                      <option key={user.id} value={user.id}>
                        {user.username}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label><FaCalendarAlt /> Termín</label>
                  <input
                    type="date"
                    value={formData.due_date}
                    onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label><FaFlag /> Priorita</label>
                  <select
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                  >
                    <option value="low">Nízka</option>
                    <option value="medium">Stredná</option>
                    <option value="high">Vysoká</option>
                    <option value="urgent">Urgentná</option>
                  </select>
                </div>
              </div>

              <div className="form-group full-width">
                <label>Popis</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows="3"
                  placeholder="Dodatočné informácie..."
                />
              </div>

              <div className="form-actions">
                <button type="submit" className="btn btn-success">
                  {editingTodo ? 'Aktualizovať' : 'Vytvoriť'}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowAddForm(false);
                    setEditingTodo(null);
                    setFormData({
                      title: '',
                      description: '',
                      category_id: null,
                      assigned_to_id: null,
                      due_date: '',
                      priority: 'medium'
                    });
                  }}
                >
                  Zrušiť
                </button>
              </div>
            </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Category Form */}
      <AnimatePresence>
        {showCategoryForm && (
          <motion.div
            className="category-form-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowCategoryForm(false)}
          >
            <motion.div
              className="category-form"
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3>Nová kategória</h3>
              <form onSubmit={handleCategorySubmit}>
                <div className="form-group">
                  <label>Názov</label>
                  <input
                    type="text"
                    value={categoryForm.name}
                    onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Farba</label>
                  <input
                    type="color"
                    value={categoryForm.color}
                    onChange={(e) => setCategoryForm({ ...categoryForm, color: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Ikona</label>
                  <select
                    value={categoryForm.icon}
                    onChange={(e) => setCategoryForm({ ...categoryForm, icon: e.target.value })}
                  >
                    <option value="📋">📋 Všeobecné</option>
                    <option value="💼">💼 Práca</option>
                    <option value="🏠">🏠 Domov</option>
                    <option value="🛒">🛒 Nákupy</option>
                    <option value="💪">💪 Cvičenie</option>
                    <option value="📚">📚 Štúdium</option>
                    <option value="🎯">🎯 Ciele</option>
                    <option value="💡">💡 Nápady</option>
                    <option value="🎨">🎨 Projekty</option>
                    <option value="📱">📱 Volania</option>
                  </select>
                </div>
                <div className="form-actions">
                  <button type="submit" className="btn btn-primary">
                    Vytvoriť
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowCategoryForm(false)}
                  >
                    Zrušiť
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Todos List */}
      <div className="todos-container">
        {loading ? (
          <div className="loading">Načítavam úlohy...</div>
        ) : Object.keys(groupedTodos).length === 0 ? (
          <div className="no-todos">
            <FaTasks />
            <p>Žiadne úlohy</p>
          </div>
        ) : (
          Object.entries(groupedTodos).map(([groupName, group]) => (
            <div key={groupName} className="todo-group">
              <div 
                className="group-header"
                onClick={() => setExpandedCategories(prev => ({
                  ...prev,
                  [groupName]: !prev[groupName]
                }))}
                style={{ cursor: 'pointer' }}
              >
                <h3>
                  <span style={{ color: group.color }}>
                    {group.icon} {groupName}
                  </span>
                  <span className="todo-count">({group.todos.length})</span>
                </h3>
                {expandedCategories[groupName] ? <FaChevronDown /> : <FaChevronRight />}
              </div>
              
              <AnimatePresence>
                {(expandedCategories[groupName] !== false) && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="todos-list"
                  >
                    {group.todos.map(todo => (
                      <motion.div
                        key={todo.id}
                        className={`todo-item ${todo.status === 'completed' ? 'completed' : ''} ${todo.is_overdue ? 'overdue' : ''}`}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        whileHover={{ scale: 1.02 }}
                      >
                        <div className="todo-checkbox">
                          <input
                            type="checkbox"
                            checked={todo.status === 'completed'}
                            onChange={() => handleStatusChange(
                              todo.id,
                              todo.status === 'completed' ? 'pending' : 'completed'
                            )}
                          />
                        </div>
                        
                        <div className="todo-content">
                          <div className="todo-header">
                            <h4 className={todo.status === 'completed' ? 'completed-text' : ''}>
                              {todo.title}
                            </h4>
                            <span 
                              className="priority-badge"
                              style={{ color: getPriorityColor(todo.priority) }}
                              title={`Priorita: ${todo.priority}`}
                            >
                              {getPriorityIcon(todo.priority)}
                            </span>
                          </div>
                          
                          {todo.description && (
                            <p className="todo-description">{todo.description}</p>
                          )}
                          
                          <div className="todo-meta">
                            {todo.due_date && (
                              <span className={`due-date ${todo.is_overdue ? 'overdue' : todo.is_today ? 'today' : ''}`}>
                                <FaCalendarAlt /> {formatDate(todo.due_date)}
                              </span>
                            )}
                            {todo.assigned_to && (
                              <span className="assigned-to">
                                <FaUser /> {todo.assigned_to}
                              </span>
                            )}
                            {todo.category && (
                              <span 
                                className="category-badge"
                                style={{ backgroundColor: todo.category.color + '20', color: todo.category.color }}
                              >
                                {todo.category.icon} {todo.category.name}
                              </span>
                            )}
                          </div>
                        </div>
                        
                        <div className="todo-actions">
                          <button
                            className="btn-icon edit"
                            onClick={() => {
                              setEditingTodo(todo.id);
                              setFormData({
                                title: todo.title,
                                description: todo.description || '',
                                category_id: todo.category_id,
                                assigned_to_id: todo.assigned_to_id,
                                due_date: todo.due_date || '',
                                priority: todo.priority
                              });
                              setShowAddForm(true);
                            }}
                          >
                            <FaEdit />
                          </button>
                          <button
                            className="btn-icon delete"
                            onClick={() => handleDelete(todo.id)}
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </motion.div>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default TodoList;